import axios from 'axios'
import { ElMessage } from 'element-plus'
import { clearAuth, clearEnrollAuth, getEnrollToken, getToken } from '@/utils/auth'

const API_BASE_URL = process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:8000/api'
const API_ORIGIN = API_BASE_URL.endsWith('/api') ? API_BASE_URL.slice(0, -4) : API_BASE_URL

const api = axios.create({
    baseURL: API_BASE_URL,
    timeout: 300000,  // 5分钟超时，用于长时间排程操作
    headers: {
        'Content-Type': 'application/json'
    }
})

// Request interceptor
api.interceptors.request.use((config) => {
    const token = getToken()
    if (token) {
        config.headers = config.headers || {}
        config.headers.Authorization = `Bearer ${token}`
    }
    return config
})

// Response interceptor
api.interceptors.response.use(
    response => {
        // 对于 blob 类型（文件下载），直接返回 data
        if (response.config.responseType === 'blob') {
            return response.data
        }
        return response.data
    },
    error => {
        const isLoginRequest = (error.config?.url || '').includes('/auth/login')
        if (error.response?.status === 401 && !isLoginRequest) {
            clearAuth()
            if (window.location.pathname !== '/login') {
                window.location.href = '/login'
            }
            ElMessage.error('登录已过期，请重新登录')
            return Promise.reject(error)
        }
        const message = error.response?.data?.detail || error.message || '请求失败'
        ElMessage.error(message)
        return Promise.reject(error)
    }
)

const enrollApi = axios.create({
    baseURL: API_BASE_URL,
    timeout: 120000,
    headers: {
        'Content-Type': 'application/json'
    }
})

enrollApi.interceptors.request.use((config) => {
    const token = getEnrollToken()
    if (token) {
        config.headers = config.headers || {}
        config.headers.Authorization = `Bearer ${token}`
    }
    return config
})

enrollApi.interceptors.response.use(
    response => response.data,
    error => {
        const url = error.config?.url || ''
        const isAuthRequest = url.includes('/enrollment/student/login') || url.includes('/enrollment/student/register')
        if (error.response?.status === 401 && !isAuthRequest) {
            clearEnrollAuth()
            if (window.location.pathname.startsWith('/enroll')) {
                window.location.href = '/enroll/login'
            }
            ElMessage.error('登录已过期，请重新登录')
            return Promise.reject(error)
        }
        const message = error.response?.data?.detail || error.message || '请求失败'
        ElMessage.error(message)
        return Promise.reject(error)
    }
)

export default api

export const ENROLL_UPLOAD_ACTION = `${API_BASE_URL}/enrollment/student/upload`

export const resolveEnrollUrl = (path) => {
    if (!path) return ''
    if (path.startsWith('http://') || path.startsWith('https://')) return path
    if (path.startsWith('/api/')) return `${API_ORIGIN}${path}`
    if (path.startsWith('/enrollment/')) return `${API_BASE_URL}${path}`
    if (path.startsWith('/')) return `${API_ORIGIN}${path}`
    return `${API_BASE_URL}/${path}`
}

// ======================== Auth APIs ========================
export const authApi = {
    login: (data) => api.post('/auth/login', data),
    me: () => api.get('/auth/me'),
    logout: () => api.post('/auth/logout'),
    getUsers: () => api.get('/auth/users'),
    createUser: (data) => api.post('/auth/users', data)
}

// ======================== Semester APIs ========================
export const semesterApi = {
    getAll: () => api.get('/semesters'),
    getCurrent: () => api.get('/semesters/current'),
    getById: (id) => api.get(`/semesters/${id}`),
    create: (data) => api.post('/semesters', data),
    update: (id, data) => api.put(`/semesters/${id}`, data),
    delete: (id) => api.delete(`/semesters/${id}`)
}

// ======================== Teacher APIs ========================
export const teacherApi = {
    getAll: () => api.get('/teachers'),
    getById: (id) => api.get(`/teachers/${id}`),
    create: (data) => api.post('/teachers', data),
    update: (id, data) => api.put(`/teachers/${id}`, data),
    delete: (id) => api.delete(`/teachers/${id}`),
    batchImport: (data) => api.post('/teachers/batch-import', data),

    // Subjects
    getSubjects: () => api.get('/teachers/subjects'),
    createSubject: (data) => api.post('/teachers/subjects', data),
    deleteSubject: (id) => api.delete(`/teachers/subjects/${id}`),

    // Unavailability
    getUnavailable: (teacherId, examId = null) =>
        api.get(`/teachers/${teacherId}/unavailable`, { params: { exam_id: examId } }),
    setUnavailable: (data) => api.post('/teachers/unavailable', data),
    removeUnavailable: (id) => api.delete(`/teachers/unavailable/${id}`)
}

// ======================== Exam APIs ========================
export const examApi = {
    getAll: (semesterId = null) =>
        api.get('/exams', { params: { semester_id: semesterId } }),
    getById: (id) => api.get(`/exams/${id}`),
    create: (data) => api.post('/exams', data),
    update: (id, data) => api.put(`/exams/${id}`, data),
    delete: (id) => api.delete(`/exams/${id}`),

    // Rooms
    getRooms: () => api.get('/exams/rooms'),
    createRoom: (data) => api.post('/exams/rooms', data),
    updateRoom: (id, data) => api.put(`/exams/rooms/${id}`, data),
    deleteRoom: (id) => api.delete(`/exams/rooms/${id}`),

    // Sessions - 新模型：场次包含科目、年级和考场
    addSession: (examId, data) => api.post(`/exams/${examId}/sessions`, data),
    batchAddSessions: (examId, data) => api.post(`/exams/${examId}/sessions/batch`, data),
    updateSession: (sessionId, data) => api.put(`/exams/sessions/${sessionId}`, data),
    deleteSession: (id) => api.delete(`/exams/sessions/${id}`),

    // Session Room Assignment - 新增
    assignRoomsToSession: (sessionId, roomIds, requiredInvigilators = 1) =>
        api.post(`/exams/sessions/${sessionId}/rooms`, roomIds, {
            params: { required_invigilators: requiredInvigilators }
        }),
    removeRoomFromSession: (sessionId, roomId) =>
        api.delete(`/exams/sessions/${sessionId}/rooms/${roomId}`),

    // Export
    exportExcel: (examId) => api.get(`/exams/${examId}/export`, { responseType: 'blob' })
}

// ======================== Schedule APIs ========================
export const scheduleApi = {
    getAll: (examId = null, sessionId = null, teacherId = null) =>
        api.get('/schedules', { params: { exam_id: examId, session_id: sessionId, teacher_id: teacherId } }),
    autoGenerate: (examId) => api.post('/schedules/auto-generate', { exam_id: examId }),
    update: (id, data) => api.put(`/schedules/${id}`, data),
    swap: (scheduleId1, scheduleId2) =>
        api.post('/schedules/swap', { schedule_id_1: scheduleId1, schedule_id_2: scheduleId2 }),
    delete: (id) => api.delete(`/schedules/${id}`),

    // Statistics
    getSemesterStats: (semesterId) => api.get(`/schedules/statistics/semester/${semesterId}`),
    getExamStats: (examId) => api.get(`/schedules/statistics/exam/${examId}`),
    getTeacherStats: (teacherId) => api.get(`/schedules/statistics/teacher/${teacherId}`)
}

// ======================== Student APIs ========================
export const studentApi = {
    getAll: (params) => api.get('/students', { params }),
    getById: (id) => api.get(`/students/${id}`),
    create: (data) => api.post('/students', data),
    update: (id, data) => api.put(`/students/${id}`, data),
    delete: (id) => api.delete(`/students/${id}`),
    batchImport: (semesterId, grade, file) => {
        const formData = new FormData()
        formData.append('file', file)
        return api.post(`/students/batch-import?semester_id=${semesterId}&grade=${encodeURIComponent(grade)}`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        })
    },
    promote: (data) => api.post('/students/promote', data),
    setGrade: (studentId, data) => api.put(`/students/${studentId}/set-grade`, data)
}

// ======================== Grade APIs ========================
export const gradeApi = {
    getAll: (params) => api.get('/grades', { params }),
    create: (data) => api.post('/grades', data),
    update: (id, data) => api.put(`/grades/${id}`, data),
    delete: (id) => api.delete(`/grades/${id}`),
    batchImport: (semesterId, examType, file, subjectTotalsJson = null) => {
        const formData = new FormData()
        formData.append('file', file)
        let url = `/grades/batch-import?semester_id=${semesterId}&exam_type=${examType}`
        if (subjectTotalsJson) {
            url += `&subject_totals=${encodeURIComponent(subjectTotalsJson)}`
        }
        return api.post(url, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        })
    },
    batch: (data) => api.post('/grades/batch', data),
    batchDelete: (semesterId, examType) => api.delete(`/grades/batch?semester_id=${semesterId}&exam_type=${examType}`),
    calculateKeyClass: (data) => api.post('/grades/key-class-analysis', data),
    exportKeyClass: (data) => api.post('/grades/key-class-analysis/export', data, { responseType: 'blob' }),

    // Evaluation Export
    uploadTemplate: (file) => {
        const formData = new FormData()
        formData.append('file', file)
        return api.post('/grades/evaluation/upload-template', formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        })
    },
    exportEvaluation: (data) => api.post('/grades/evaluation/export', data, { responseType: 'blob' })
}

// ======================== Grade Analysis APIs ========================
export const gradeAnalysisApi = {
    getOverview: (semesterId, examType) =>
        api.get('/grades/analysis/overview', { params: { semester_id: semesterId, exam_type: examType } }),
    getStudentRankings: (data) => api.post('/grades/analysis/student-rankings', data),
    exportStudentRankings: (data) => api.post('/grades/analysis/student-rankings/export', data, { responseType: 'blob' })
}

// ======================== Class Teacher Assignment APIs ========================
export const classTeacherApi = {
    getAll: (semesterId) => api.get('/class-teachers', { params: { semester_id: semesterId } }),
    batchSave: (semesterId, data) => api.post(`/class-teachers/batch?semester_id=${semesterId}`, data),
    delete: (id) => api.delete(`/class-teachers/${id}`)
}

// ======================== Enrollment Student APIs ========================
export const enrollmentStudentApi = {
    register: (data) => enrollApi.post('/enrollment/student/register', data),
    login: (data) => enrollApi.post('/enrollment/student/login', data),
    me: () => enrollApi.get('/enrollment/student/me'),
    logout: () => enrollApi.post('/enrollment/student/logout'),
    getForm: () => enrollApi.get('/enrollment/student/form'),
    saveDraft: (data) => enrollApi.patch('/enrollment/student/form/draft', data),
    saveForm: (data) => enrollApi.put('/enrollment/student/form', data),
    getStatus: () => enrollApi.get('/enrollment/student/status')
}

// ======================== Enrollment Admin APIs ========================
export const enrollmentAdminApi = {
    getStudents: (params) => api.get('/enrollment/admin/students', { params }),
    exportStudents: (params) => api.get('/enrollment/admin/students/export', { params, responseType: 'blob' }),
    getStudentDetail: (id) => api.get(`/enrollment/admin/students/${id}`),
    reviewStudent: (id, status) => api.post(`/enrollment/admin/students/${id}/review`, { status }),
    getStatistics: () => api.get('/enrollment/admin/statistics'),
    resetStudentPassword: (id) => api.post(`/enrollment/admin/students/${id}/reset-password`)
}
